import type {
  PermissionType,
  PermissionMetadata,
  PermissionResult
} from '../types'
import { BasePermissionStrategy } from './base.strategy'
import { Service } from '@/services/helpers/ServiceHelper'

export class NotificationsStrategy extends BasePermissionStrategy {
  readonly type: PermissionType = 'notifications'

  readonly metadata: PermissionMetadata = {
    type: 'notifications',
    title: 'Notifications',
    description:
        'We use notifications to allow you to receive messages from your Emissary contacts. Please grant us permission to send you notifications.',
    deniedTitle: 'Notifications Disabled',
    deniedDescription:
        'To enable notifications, please go to your device settings and allow notifications for Emissary.'
  }

  isSupported (): boolean {
    if (this.context.isMobile) {
      return true
    }
    return typeof navigator !== 'undefined' && 'Notification' in window
  }

  async check (): Promise<PermissionResult> {
    if (!this.isSupported()) {
      return this.createResult('unsupported')
    }

    if (this.context.isMobile) {
      return await this.checkMobile()
    }
    return await this.checkWeb()
  }

  async request (): Promise<PermissionResult> {
    if (!this.isSupported()) {
      return this.createResult('unsupported')
    }

    if (this.context.isMobile) {
      return await this.requestMobile()
    }
    return await this.requestWeb()
  }

  // === WEB ===
  private checkWeb (): PermissionResult {
    const permission = Notification.permission
    const stateMap: Record<
        NotificationPermission,
        'granted' | 'denied' | 'prompt'
      > = {
        granted: 'granted',
        denied: 'denied',
        default: 'prompt'
      }
    return this.createResult(stateMap[permission])
  }

  private async requestWeb (): Promise<PermissionResult> {
    try {
      const permission = await Notification.requestPermission()
      return this.createResult(permission === 'granted' ? 'granted' : 'denied')
    } catch {
      return this.createResult('denied')
    }
  }

  // === MOBILE ===
  private async checkMobile (): Promise<PermissionResult> {
    try {
      const result =
          await Service.capacitor.permissionsCapacitor?.checkPermission(
            'notifications'
          )
      if (!result) {
        return this.createResult('unsupported')
      }
      return this.createResult(result.state)
    } catch {
      return this.createResult('unsupported')
    }
  }

  private async requestMobile (): Promise<PermissionResult> {
    try {
      const result =
          await Service.capacitor.permissionsCapacitor?.requestPermission(
            'notifications'
          )
      if (!result) {
        return this.createResult('unsupported')
      }
      return this.createResult(result.state)
    } catch {
      return this.createResult('denied')
    }
  }
}
